import { TData, TSearch, TStore } from "@components/types";
import { getSafeUrl } from "@core/utils";
import { AutoComplete, Input } from "antd";
import { BedDouble, Layers, Package, Search, Store, Tag } from "lucide-react";
import { useRouter } from "next/router";
import React, { useEffect, useRef, useState } from "react";

import { getSearch } from "@api/graphql/search";
import { getLocation } from "@core/geolocation";

interface Props {
    mode?: string;
    callback?: Function;
    theme?: string;
}

const renderTitle = (title: string, identifier: string, url: string) => (
    <div className="text-neutral-700 font-semibold md:text-md text-sm">
        {title}
        <span className="text-brand_cerulean">
            <a
                className="font-medium font-manrope"
                style={{ float: "right" }}
                href={`/${url}`}
            >
                more
            </a>
        </span>
    </div>
);

const urlMap: Map<string, string> = new Map<string, string>([
    ["store", "store"],
    ["shop", "shop"],
    ["stays", "stays"],
    ["product", "product"],
    ["productcategory", "category"],
    ["brand", "brand"],
]);

const iconFor = (category: string, subcategory_id: number | null) => {
    const cls = "w-4 h-4 text-neutral-400 flex-shrink-0";
    if (category === "store") return subcategory_id == 29 ? <BedDouble className={cls} /> : <Store className={cls} />;
    if (category === "productcategory") return <Layers className={cls} />;
    if (category === "brand") return <Tag className={cls} />;
    return <Package className={cls} />;
};

const hrefFor = (title: string, id: any, category: string, category_id: number = null, subcategory_id: number = null) => {
    if (id == 0) return `/${category}`;
    if (category === "product") return `/product/all?q=${encodeURIComponent(title)}&intent=product`;
    const route = urlMap.get((category === "store" && subcategory_id == 29) ? "stays" : (category === "store" && category_id === 1) ? "shop" : category);
    return `/${route}/${id}/${getSafeUrl(title)}`;
};

const renderItem = (title: string, id: any, category: string, category_id: number = null, subcategory_id: number = null, count: number = 1, place: string = "") => {
    return {
        // The same name can appear in several groups (a store and a product), so keys must not rely on the title alone.
        key: `${category}-${id ?? title}`,
        value: title,
        href: hrefFor(title, id, category, category_id, subcategory_id),
        label: (
            <div className="flex items-center gap-2 min-w-0">
                {id == 0 ? null : iconFor(category, subcategory_id)}
                <span className="min-w-0 flex flex-col leading-tight">
                    <span className="text-sm text-neutral-700 truncate">{title}</span>
                    {place && <span className="text-xs text-neutral-400 truncate">{place}</span>}
                </span>
                {category === "store" && subcategory_id == 29 && (
                    <span className="ml-auto text-[11px] font-semibold text-sky-600">Stay</span>
                )}
                {category === "product" && count > 1 && (
                    <span className="ml-auto text-[11px] text-neutral-400">{count} listings</span>
                )}
            </div>
        ),
    };
};

const initialOptions = new Map<string, any>([
    [
        "product",
        {
            label: renderTitle("Products", "product", "product/trending"),
            options: [renderItem("Trending Products", 0, "product/trending")],
        },
    ],
    [
        "store",
        {
            label: renderTitle("Stores", "store", "stores"),
            options: [renderItem("Nearby Stores", 0, "stores")],
        },
    ],
    [
        "productcategory",
        {
            label: renderTitle("Product Category", "productcategory", "category"),
            options: [renderItem("Trending Categories", 0, "category")],
        },
    ],
    [
        "brand",
        {
            label: renderTitle("Brands", "brand", "brand/explore"),
            options: [renderItem("Trending Brands", 0, "brand/explore")],
        },
    ],
]);

export const Searchbar: React.FC<Props> = ({ mode, callback, theme }) => {
    const [query, setQuery] = useState<TSearch>();
    let curRunId = useRef(0);
    const router = useRouter();
    const [options, setOptions] = useState<Map<string, any>>();
    const [searchInput, setSearchInput] = useState("");
    const updateOptions = (identifier: string, result: any[]) => {
        return {
            ...initialOptions.get(identifier),
            options: result
                .map((x: any) => renderItem(x?.name, x?.id, identifier, x?.category_id, x?.sub_category_id, identifier == "product" ? (x?.matches || 1) : 1, x?.place))
                .slice(0, 5),
        };
    };
    useEffect(() => {
        if (searchInput.length == 0) {
            setOptions(initialOptions);
        }
    }, [searchInput]);

    useEffect(() => {
        let q = (router.query?.q as string) || "";
        let intent = router.query.intent as string;

        if (intent != undefined || mode != undefined) {
            let lookup = intent || mode;

            setOptions(new Map([[lookup, initialOptions.get(lookup)]]));
        } else {
            setOptions(initialOptions);
        }
        setQuery({
            ...query,
            descriptor: { ...query?.descriptor, q: q, intent: intent || "product" },
        });
        // Show the current search term (e.g. on the results page) instead of an empty box.
        setSearchInput(q);
    }, [router.isReady, router.query?.q]);

    const fetchSearch = (query: string, intent: string, context: any, runId: number) => {
        getSearch(query, intent, context)
            .then((response) => {
                if (runId == curRunId.current) {
                    const storeValues = response?.get_stores?.map((store: TStore) => ({
                        name: store.name,
                        id: store.id,
                        category_id: store.category_id,
                        sub_category_id: store.sub_category_id,
                        place: [store.address?.city?.name, store.address?.state?.name].filter(Boolean).join(", "),
                    }));
                    const productValues = response?.get_unique_products?.map((product: TData) => ({
                        name: product.name,
                        matches: product.matches,
                    }));
                    const productCategoryValues = response?.get_productcategories?.map(
                        (productcategory: TData) => ({
                            name: productcategory.name,
                            id: productcategory.id,
                        })
                    );
                    const brandValues = response?.get_brands?.map(
                        (brand: TData) => ({
                            name: brand.name,
                            id: brand.id,
                        })
                    );

                    let newOptions = new Map<string, any>();
                    if (storeValues?.length > 0) {
                        newOptions.set("store", updateOptions("store", storeValues));
                    }
                    if (productValues?.length > 0) {
                        newOptions.set("product", updateOptions("product", productValues));
                    }
                    if (productCategoryValues?.length > 0) {
                        newOptions.set(
                            "productcategory",
                            updateOptions("productcategory", productCategoryValues)
                        );
                    }
                    if (brandValues?.length > 0) {
                        newOptions.set("brand", updateOptions("brand", brandValues)
                        );
                    }
                    setOptions(newOptions);
                }
            }).catch((err) =>
                console.error("Error", err)
            );
    }

    useEffect(() => {
        curRunId.current = curRunId.current + 1
        if (query?.descriptor?.q !== undefined && query?.descriptor?.q !== "") {
            const intent = mode !== undefined ? mode : "all"; // Set the intent to "all" if mode is undefined
            fetchSearch(query?.descriptor?.q, intent, query?.context, curRunId.current);
        }
    }, [query?.descriptor?.q]);

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

    const handleSearch = (q: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            let location = await getLocation();
            setQuery((prev) => ({
                ...prev,
                descriptor: { ...prev?.descriptor, q: q },
                context: { lat: location?.latitude, lon: location?.longitude },
            }));
        }, 250);
    };

    const runTypedSearch = (typed: string) => {
        const q = typed.trim();
        if (q) router.push(`/product/all?q=${encodeURIComponent(q)}&intent=product`);
    };

    const handleSelect = async (element: any, option: any) => {
        if (option?.href != undefined) {
            router.push(option.href)
        }
    }

    return (
        <div className="search flex flex-row w-full grow-1 justify-center rounded-lg">
            <AutoComplete
                defaultActiveFirstOption={false}
                popupClassName="p-1"
                style={{ width: "100%", borderRadius: "2px" }}
                popupMatchSelectWidth={true}
                options={Array.from(options === undefined ? [] : options.values())}
                notFoundContent={options && options.size === 0 && "No results found. Press Enter to search products."}
                value={searchInput}
                onChange={(value: string) => setSearchInput(value || "")}
                onSearch={handleSearch}
                onSelect={handleSelect}
            >
                <Input
                    size="middle"
                    onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        // A highlighted suggestion is handled by the dropdown (onSelect); with none highlighted,
                        // Enter runs a full product search for what was typed.
                        if (document.querySelector(".ant-select-item-option-active")) return;
                        runTypedSearch((e.target as HTMLInputElement).value);
                    }}
                    placeholder={
                        mode == "product"
                            ? "Search Products in store"
                            : "Search Shop or Product"
                    }
                    className="text-neutral-600"
                    allowClear
                    prefix={
                        <button
                            type="button"
                            aria-label="Search"
                            onClick={() => runTypedSearch(searchInput)}
                            className="p-0 border-0 bg-transparent cursor-pointer flex items-center text-neutral-600"
                        >
                            <Search className="font-semibold" size="1.5em" />
                        </button>
                    }
                />
            </AutoComplete>
        </div>
    );
};
